import React, {
  RefObject,
} from 'react';
import Table from 'core/components/VirtualTable';
import {
  useMedia,
  useNotify,
  useSidePanel,
  useTableReset,
} from 'core/hooks';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  useSyncDateRange,
} from 'pages/Dashboard/hooks/url';
import DateRangePicker from 'core/components/DateTimePicker/DateRangePicker';
import useSignalREffect from 'views/EMR/hooks/signalR';
import columns from 'pages/Dashboard/pages/Billing/Statements/columns';
import {
  BulkPatientStatementDTO,
} from 'dtos/bulkPatientStatementDTO';
import {
  usePrepareStatement,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  getStatementList,
} from 'pages/Dashboard/pages/Billing/api';
import PrepareStatementButton from 'pages/Dashboard/pages/Billing/Statements/PrepareStatementButton';
import {
  buildStatementPanelInfo,
  canOpenRun,
  isRunPanelOpen,
  RUN_OUTPUT_PANEL_TYPE,
} from 'views/EMR/components/Panel/RunOutputPanel/utils';
import {
  Row,
} from '@tanstack/react-table';

export default function Statements() {
  const [dateRange, setDateRange] = useSyncDateRange({ uniqueKey: 'statementsDateRange' });
  const { reset, tableInstanceRef } = useTableReset();
  const { requestMedia } = useMedia();
  const { activePanel, openPanel } = useSidePanel();

  useSignalREffect('OnBulkPatientStatementCompleted', () => {
    tableInstanceRef?.current?.refetch?.();
  });

  const {
    mutateAsync: prepareStatement,
    isPending: isLoading,
  } = usePrepareStatement();

  const notify = useNotify();
  const onPrepareStatement = async (sendEmail?: boolean) => {
    await prepareStatement({ sendEmail });
    await tableInstanceRef?.current?.refetch?.();
    notify({
      variant: 'success',
      message: 'Bulk statements are being prepared. Please check back in a few minutes.',
    });
  };

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex justify-between items-center mb-4 px-4">
        <DateRangePicker
          value={dateRange}
          onChange={(nextDateRange) => {
            if (nextDateRange) {
              setDateRange(nextDateRange);
              reset();
            }
          }}
        />
        <PrepareStatementButton
          isLoading={isLoading}
          onPrepareStatement={onPrepareStatement}
        />
      </div>
      <div className="relative flex flex-col flex-1 overflow-y-hidden">
        <div className="h-full w-full">
          <Table<BulkPatientStatementDTO>
            instanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
            getData={getStatementList}
            getRowId={(row) => row?.printStatementReportId}
            queryParams={{ dateRange, requestMedia }}
            columns={columns}
            emptySlot="No Statements Found"
            onRowClick={(_table, row) => {
              if (canOpenRun(row.original.reportState)) {
                openPanel(RUN_OUTPUT_PANEL_TYPE, buildStatementPanelInfo(row.original));
              }
            }}
            defaultPagination={{
              pageSize: 25,
              pageIndex: 0,
            }}
            classes={{
              header: 'px-4',
              outerRow: '!w-[calc(100%-16px)] left-4',
              row: (row: Row<BulkPatientStatementDTO>) => (
                isRunPanelOpen(activePanel, {
                  kind: 'statement',
                  bulkPatientStatementId: row.original.printStatementReportId ?? 0,
                })
                  ? 'bg-blue-50'
                  : ''
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
}
